<template>
  <div class="chatbot-container" :class="{ minimized }">
    <!-- Minimized state: Circular icon -->
    <div v-if="minimized" class="minimized-icon" @click="toggleChat">
      <!-- Use um ícone do Font Awesome aqui. Certifique-se de que o Font Awesome está configurado no seu projeto. -->
      <!-- Exemplo: ícone de balão de chat -->
      <i class="fas fa-comment-dots"></i>
    </div>

    <!-- Expanded state: Full chat window -->
    <div v-if="!minimized" class="expanded-chat">
      <div class="chat-header" @click="toggleChat" style="display: flex; flex-direction: row">
        <div style=""><i class="fas fa-comment-dots"></i> <span>Chat</span></div>
        <div style="align-self: center; margin-left: auto">X</div>
      </div>
      <div class="chat-content">
        <div class="messages">
          <div v-for="message in messages" :key="message.id" :class="['message', message.type]">
            <p>{{ message.text }}</p>
          </div>
        </div>
        <div class="chat-input">
          <input
            v-model="userMessage"
            @keyup.enter="sendMessage"
            placeholder="Digite sua mensagem..."
          />
          <button @click="sendMessage">Enviar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue'
import axios from 'axios'

export default defineComponent({
  name: 'ChatBot',
  setup() {
    const minimized = ref(true)
    const messages = ref([{ id: 1, text: 'Olá, como posso ajudar?', type: 'bot' }])
    const userMessage = ref('')

    // URL do webhook do Dialogflow
    // filepath: /home/heremit/Desktop/lugand/lugand-web/src/components/ChatBot.vue
    const dialogflowWebhookUrl = 'https://dialogflow.googleapis.com/v2/...'

    const toggleChat = () => {
      minimized.value = !minimized.value
    }

    const sendMessage = async () => {
      if (userMessage.value.trim() === '') return

      // Adiciona a mensagem do usuário na tela
      messages.value.push({ id: Date.now(), text: userMessage.value, type: 'user' })

      try {
        // Envia a mensagem para o Dialogflow
        const response = await axios.post(dialogflowWebhookUrl, {
          queryInput: {
            text: {
              text: userMessage.value,
              languageCode: 'pt-BR'
            }
          }
        })

        const botMessage = response.data.fulfillmentText
        // Adiciona a resposta do bot
        messages.value.push({ id: Date.now(), text: botMessage, type: 'bot' })
      } catch (error) {
        messages.value.push({ id: Date.now(), text: 'Desculpe, algo deu errado.', type: 'bot' })
      } finally {
        userMessage.value = ''
      }
    }

    return {
      minimized,
      messages,
      userMessage,
      toggleChat,
      sendMessage
    }
  }
})
</script>

<style scoped>
.chatbot-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  overflow: hidden;
  z-index: 999;
  box-shadow: 0px 0px 15px 5px rgba(0, 0, 0, 0.2);
}

/* Minimized state styles */
.chatbot-container.minimized {
  width: 60px; /* Tamanho pequeno para o ícone */
  height: 60px; /* Tamanho pequeno para o ícone */
  border-radius: 50%; /* Torna-o circular */
  display: flex; /* Centraliza o ícone */
  justify-content: center; /* Centraliza o ícone */
  align-items: center; /* Centraliza o ícone */
  cursor: pointer;
  background-color: var(--color-border); /* Cor de fundo para o ícone */
  color: aliceblue; /* Cor do ícone */
  box-shadow: 0px 0px 10px 3px rgba(0, 0, 0, 0.3); /* Ajusta a sombra para o estado minimizado */
  animation: rgbShadow 8s infinite alternate;
}

.minimized-icon {
  font-size: 24px; /* Tamanho do ícone */
}

/* Expanded state styles */
.expanded-chat {
  width: 300px; /* Largura original */
  height: 400px; /* Altura original ou máxima */
  display: flex;
  flex-direction: column;
  background-color: aliceblue; /* Cor de fundo para o contêiner expandido */
  border-radius: 10px; /* Mantém cantos arredondados para o estado expandido */
}

.chat-header {
  background-color: var(--color-border);
  color: aliceblue;
  padding: 10px;
  cursor: pointer;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

.chat-content {
  color: var(--color-text);
  background-color: var(--color-background);
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  max-height: calc(400px - 40px);
}

.messages {
  flex-grow: 1;
  overflow-y: auto;
  padding: 10px;
}

.message {
  margin-bottom: 10px;
  border-radius: 5px;
  padding-inline: 5px;
  word-wrap: break-word;
}

.message.user {
  color: aliceblue;
  text-align: right;
  margin-left: 15px;
  background-color: var(--color-border);
}

.message.bot {
  text-align: left;
  margin-right: 15px;
  background-color: var(--color-shadow-background);
}

.chat-input {
  display: flex;
  padding: 10px;
  border-top: 1px solid var(--color-border);
}

.chat-input input {
  flex-grow: 1;
  padding: 5px;
  border: 1px solid var(--color-border);
  border-radius: 5px;
}

.chat-input button {
  background-color: var(--color-border);
  color: aliceblue;
  border: none;
  padding: 5px 10px;
  margin-left: 5px;
  cursor: pointer;
  border-radius: 5px;
}

@media (max-width: 768px) {
  .chatbot-container {
    bottom: 20px;
    right: 10px;
  }
  .expanded-chat {
    width: 300px;
  }
}
</style>
